/**
 * Snusify facets: in-place filtering, sorting and "load more" over a form
 * that already works on its own.
 *
 * The markup is a GET form against the results URL, so every control works
 * with scripting off. This upgrades it: a change is fetched through the
 * Section Rendering API for this same section, and only the parts that
 * changed are swapped - the grid, the pills, each panel's options and each
 * chip's count. The panel elements themselves are never replaced, so a panel
 * that is open stays open while its counts update underneath the pointer.
 *
 * The result count is a live region that lives across updates; only its text
 * is rewritten, because a newly inserted live region is not announced.
 *
 * Pages joined by "load more" are remembered for the tab, with the scroll
 * position, so coming Back from a product rebuilds the same long list and
 * lands where the visitor left it, instead of on the first page.
 */

const MOBILE = window.matchMedia('(max-width: 749px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const DENSITY_KEY = 'snusify:grid-columns';
const MORE_KEY = 'snusify:more:';

class SnusifyFacets extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {AbortController | null} */
  #request = null;
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #listeners = null;
  #sectionId = '';
  /** How many result pages the grid holds, joined by "load more". */
  #pages = 1;

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.#sectionId = this.dataset.sectionId ?? '';
    this.#form = this.querySelector('[data-facets-form]');
    const { signal } = this.#listeners;

    this.#initPanels(signal);
    this.#initDensity(signal);

    if (this.#form) {
      this.#form.addEventListener('change', (event) => this.#onChange(event), { signal });
      this.#form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    }

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    window.addEventListener('popstate', () => this.#navigate(window.location.href, { push: false }), { signal });
    window.addEventListener('pagehide', () => this.#rememberPages(), { signal });

    this.#restorePages();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
  }

  // ---------------------------------------------------------------- panels

  /** Pin each popover under its chip on wide screens; phones get a sheet. */
  #initPanels(signal) {
    for (const panel of this.querySelectorAll('[data-facet-panel]')) {
      const chip = this.querySelector(`[popovertarget="${CSS.escape(panel.id)}"]`);
      if (!chip) continue;
      chip.setAttribute('aria-expanded', 'false');
      chip.setAttribute('aria-controls', panel.id);

      panel.addEventListener(
        'toggle',
        (event) => {
          const open = event.newState === 'open';
          chip.setAttribute('aria-expanded', String(open));
          if (open) this.#anchor(panel, chip);
        },
        { signal }
      );
    }

    const reanchor = () => {
      for (const panel of this.querySelectorAll('[data-facet-panel]:popover-open')) {
        const chip = this.querySelector(`[popovertarget="${CSS.escape(panel.id)}"]`);
        if (chip) this.#anchor(panel, chip);
      }
    };
    window.addEventListener('resize', reanchor, { passive: true, signal });
    this.querySelector('.snusify-toolbar__filters')?.addEventListener('scroll', reanchor, { passive: true, signal });
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} chip
   */
  #anchor(panel, chip) {
    if (MOBILE.matches) {
      panel.removeAttribute('data-anchored');
      panel.style.removeProperty('top');
      panel.style.removeProperty('left');
      return;
    }
    const gap = 8;
    const margin = 16;
    const rect = chip.getBoundingClientRect();
    panel.setAttribute('data-anchored', '');
    const width = panel.offsetWidth;
    const left = Math.min(Math.max(rect.left, margin), window.innerWidth - width - margin);
    panel.style.top = `${Math.round(rect.bottom + gap)}px`;
    panel.style.left = `${Math.round(left)}px`;
  }

  // ---------------------------------------------------------------- density

  /** Two, three or four across on wide screens, three to start with,
      remembered per visitor. */
  #initDensity(signal) {
    const group = this.querySelector('[data-density]');
    if (!group) return;
    group.hidden = false;

    const options = [...group.querySelectorAll('[data-cols]')];
    const apply = (cols, { save } = { save: true }) => {
      this.style.setProperty('--snusify-cols', cols);
      for (const option of options) {
        const on = option.dataset.cols === String(cols);
        option.setAttribute('aria-checked', String(on));
        option.tabIndex = on ? 0 : -1;
      }
      if (!save) return;
      try {
        localStorage.setItem(DENSITY_KEY, String(cols));
      } catch {
        // Storage can be unavailable (private mode, blocked site data); the
        // choice simply does not persist.
      }
    };

    let stored = null;
    try {
      stored = localStorage.getItem(DENSITY_KEY);
    } catch {
      stored = null;
    }
    apply(options.some((o) => o.dataset.cols === stored) ? stored : '3', { save: false });

    for (const option of options) {
      option.addEventListener('click', () => apply(option.dataset.cols), { signal });
    }

    // A radio group moves with the arrow keys and selects as it goes.
    group.addEventListener(
      'keydown',
      (event) => {
        const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!delta) return;
        event.preventDefault();
        const current = options.findIndex((o) => o.getAttribute('aria-checked') === 'true');
        const next = options[(current + delta + options.length) % options.length];
        apply(next.dataset.cols);
        next.focus();
      },
      { signal }
    );
  }

  // ---------------------------------------------------------------- events

  /**
   * Boxes and the sort apply the moment they change. Price fields fire
   * "change" only when their edit is committed - Enter or leaving the field -
   * so typing "12" never fetches for "1" first.
   * @param {Event} event
   */
  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    this.#apply();
  }

  /** @param {SubmitEvent} event */
  #onSubmit(event) {
    event.preventDefault();
    const panel = event.submitter?.closest('[data-facet-panel]');
    if (panel?.matches(':popover-open')) panel.hidePopover();
    this.#apply();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

    const link = event.target.closest('a[data-facet-link]');
    if (link) {
      event.preventDefault();
      link.closest('[data-facet-panel]')?.hidePopover?.();
      this.#navigate(link.href);
      return;
    }

    const more = event.target.closest('a[data-facets-next]');
    if (more) {
      event.preventDefault();
      this.#loadMore(more, { focus: true });
    }
  }

  // ---------------------------------------------------------------- fetching

  /** Build the URL the form describes, dropping empty fields. */
  #apply() {
    if (!this.#form) return;
    const url = new URL(this.#form.action, window.location.origin);
    const sort = this.#form.querySelector('[data-facets-sort]');

    for (const [name, value] of new FormData(this.#form)) {
      if (typeof value !== 'string' || value.trim() === '') continue;
      if (name === 'sort_by' && sort && value === sort.dataset.default) continue;
      url.searchParams.append(name, value.trim());
    }
    this.#navigate(url.toString());
  }

  /**
   * Fetch this section at `href` and swap in what changed.
   * @param {string} href
   * @param {{ push?: boolean }} [options]
   */
  async #navigate(href, { push = true } = {}) {
    const doc = await this.#fetchSection(href);
    if (!doc) return;

    const fresh = doc.querySelector('snusify-facets');
    if (!fresh) return;

    const focusId = this.contains(document.activeElement) ? document.activeElement.id : '';

    this.#swap(fresh);
    this.#pages = 1;

    if (push) {
      const clean = new URL(href, window.location.origin);
      clean.searchParams.delete('section_id');
      window.history.pushState({}, '', clean);
    }

    if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    this.#revealResults();
  }

  /**
   * @param {string} href
   * @returns {Promise<Document | null>}
   */
  async #fetchSection(href) {
    this.#request?.abort();
    this.#request = new AbortController();

    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', this.#sectionId);

    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      // Anything else: fall back to a normal page load, which cannot fail
      // the same way.
      window.location.assign(href);
      return null;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {Element} fresh */
  #swap(fresh) {
    const replaceInner = (selector) => {
      const current = this.querySelector(selector);
      const next = fresh.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    };

    replaceInner('[data-facets-region="results"]');

    // Pills: content and visibility both change.
    const pills = this.querySelector('[data-facets-region="pills"]');
    const nextPills = fresh.querySelector('[data-facets-region="pills"]');
    if (pills && nextPills) {
      pills.innerHTML = nextPills.innerHTML;
      pills.hidden = nextPills.hidden;
    }

    // The live region keeps its node; only its words change.
    const count = this.querySelector('[data-facets-count]');
    const nextCount = fresh.querySelector('[data-facets-count]');
    if (count && nextCount) count.textContent = nextCount.textContent.trim();

    // Each panel's options and reset link, and each chip's count, in place.
    for (const body of this.querySelectorAll('[data-facet-body]')) {
      const next = fresh.querySelector(`[data-facet-body="${body.dataset.facetBody}"]`);
      if (next) body.innerHTML = next.innerHTML;
    }
    for (const panel of this.querySelectorAll('[data-facet-panel]')) {
      const next = fresh.querySelector(`[data-facet-panel="${panel.dataset.facetPanel}"]`);
      const reset = panel.querySelector('.snusify-fpanel__reset');
      const nextReset = next?.querySelector('.snusify-fpanel__reset');
      if (reset && nextReset) reset.href = nextReset.href;
    }
    for (const chip of this.querySelectorAll('[data-facet-chip]')) {
      const next = fresh.querySelector(`[data-facet-chip="${chip.dataset.facetChip}"]`);
      if (!next) continue;
      chip.classList.toggle('is-active', next.classList.contains('is-active'));
      const label = next.getAttribute('aria-label');
      if (label) chip.setAttribute('aria-label', label);
      else chip.removeAttribute('aria-label');
      const badge = chip.querySelector('[data-facet-chip-count]');
      const nextBadge = next.querySelector('[data-facet-chip-count]');
      if (badge && nextBadge) {
        badge.textContent = nextBadge.textContent;
        badge.hidden = nextBadge.hidden;
      }
    }

    const sort = this.querySelector('[data-facets-sort]');
    const nextSort = fresh.querySelector('[data-facets-sort]');
    if (sort && nextSort) sort.value = nextSort.value;
  }

  /** If the visitor had scrolled past the top of the grid, bring it back. */
  #revealResults() {
    const results = this.querySelector('[data-facets-region="results"]');
    const toolbar = this.querySelector('.snusify-toolbar');
    if (!results) return;
    const offset = toolbar ? toolbar.getBoundingClientRect().height : 0;
    if (results.getBoundingClientRect().top >= offset) return;
    const top = results.getBoundingClientRect().top + window.scrollY - offset - 16;
    window.scrollTo({ top, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  }

  // ---------------------------------------------------------------- load more

  /**
   * Append the next page's cards instead of leaving for it. The link is a
   * real next-page link, so this is only ever a shortcut.
   * @param {HTMLAnchorElement} link
   * @param {{ focus: boolean }} options - move focus to the first new
   *   product, as a visitor who asked for more expects; not when rebuilding
   * @returns {Promise<boolean>} whether cards were added
   */
  async #loadMore(link, { focus }) {
    const doc = await this.#fetchSection(link.href);
    if (!doc) return false;

    const grid = this.querySelector('[data-facets-grid]');
    const nextGrid = doc.querySelector('[data-facets-grid]');
    const more = this.querySelector('[data-facets-more]');
    const nextMore = doc.querySelector('[data-facets-more]');
    if (!grid || !nextGrid) return false;

    const firstNew = nextGrid.firstElementChild;
    grid.append(...nextGrid.children);
    this.#pages += 1;

    if (more && nextMore) {
      more.replaceWith(nextMore);
      // A previous-page link makes no sense once the pages are joined, and
      // the range now starts at the first card, not at this page's first.
      nextMore.querySelector('[data-facets-prev]')?.remove();
      const progress = nextMore.querySelector('[data-joined]');
      const text = progress?.querySelector('[data-more-text]');
      if (progress && text) text.textContent = progress.dataset.joined;
    } else {
      more?.remove();
    }

    // Keyboard and screen reader users land on the first new product.
    if (focus) firstNew?.querySelector('a[href]')?.focus({ preventScroll: false });
    return true;
  }

  // ---------------------------------------------------------------- back

  #pagesKey() {
    return MORE_KEY + window.location.pathname + window.location.search;
  }

  /** On the way out, note how long the list was and where the visitor was. */
  #rememberPages() {
    try {
      if (this.#pages > 1) {
        sessionStorage.setItem(this.#pagesKey(), JSON.stringify({ pages: this.#pages, y: Math.round(window.scrollY) }));
      } else {
        sessionStorage.removeItem(this.#pagesKey());
      }
    } catch {
      // Without storage, Back simply lands on the first page.
    }
  }

  /**
   * Coming Back (or reloading), join the same pages again and return to the
   * same place. A fresh visit starts from the first page, as its link says.
   */
  async #restorePages() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.#pagesKey()) ?? 'null');
    } catch {
      return;
    }
    if (!saved || !(saved.pages > 1)) return;

    const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (performance.getEntriesByType('navigation')[0]);
    if (navigation?.type !== 'back_forward' && navigation?.type !== 'reload') {
      try {
        sessionStorage.removeItem(this.#pagesKey());
      } catch {
        // Nothing to clear.
      }
      return;
    }

    // The browser would put the scroll back before the list is long enough
    // to hold it; take that over until the pages are in.
    const restoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    try {
      while (this.#pages < saved.pages) {
        const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('a[data-facets-next]'));
        if (!link || !(await this.#loadMore(link, { focus: false }))) break;
      }
      window.scrollTo(0, Number(saved.y) || 0);
    } finally {
      history.scrollRestoration = restoration;
    }
  }
}

if (!customElements.get('snusify-facets')) {
  customElements.define('snusify-facets', SnusifyFacets);
}
